import React, { useCallback, useRef, useState } from "react";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { UseMutationResult } from "@tanstack/react-query";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { financialAccountContractClient } from "@api/financialAccount.api";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    EntityChangeWarnings,
    EntityChangeWarningMessage,
} from "./EntityChangeWarnings";

interface UseEntityChangeWithWarningsParams {
    account: FinancialAccount;
    mutation: UseMutationResult<
        FinancialAccount,
        unknown,
        Entity,
        Entity | null | undefined
    >;
    onSuccess?: () => void;
}

export function useEntityChangeWithWarnings({
    account,
    mutation,
    onSuccess,
}: UseEntityChangeWithWarningsParams) {
    const [pendingEntity, setPendingEntity] = useState<Entity | null>(null);
    const [warnings, setWarnings] = useState<EntityChangeWarnings | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isVerifyingEntityChange, setIsVerifyingEntityChange] =
        useState(false);
    const entityChangeWarningRequestIdRef = useRef(0);
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const dismiss = useCallback(() => {
        setPendingEntity(null);
        setWarnings(null);
        setErrorMessage(null);
    }, []);

    const performEntityChange = useCallback(
        (entity: Entity) => {
            mutation.mutate(entity, {
                onSuccess: () => {
                    dismiss();
                    onSuccess?.();
                },
                onError: () => {
                    setPendingEntity(entity);
                    setErrorMessage(
                        "Failed to change account entity. Please try again.",
                    );
                },
            });
        },
        [mutation, onSuccess, dismiss],
    );

    const initiateEntityChange = useCallback(
        async (entity: Entity) => {
            // Refused before the warnings precheck rather than after: that GET
            // requires admin access, so a view-only user gets a bare 404 whose
            // "Not Found" toast and generic "cannot verify" modal both describe
            // the wrong problem (PRO-11391).
            if (refuseIfViewOnly()) {
                return;
            }

            const requestId = ++entityChangeWarningRequestIdRef.current;

            if (!account.entityId) {
                dismiss();
                performEntityChange(entity);
                return;
            }

            setIsVerifyingEntityChange(true);
            try {
                const response =
                    await financialAccountContractClient.getEntityChangeWarnings(
                        {
                            params: {
                                financialAccountId: account.id,
                            },
                            query: { entityId: entity.id },
                        },
                    );

                if (requestId !== entityChangeWarningRequestIdRef.current) {
                    return;
                }

                const data = response.body;

                if (data.transferError) {
                    setPendingEntity(entity);
                    setWarnings(data);
                    setErrorMessage(null);
                    return;
                }

                if (
                    data.hasDocumentsToTransfer ||
                    data.hasJournalEntriesToTransfer
                ) {
                    setPendingEntity(entity);
                    setWarnings(data);
                    setErrorMessage(null);
                } else {
                    dismiss();
                    performEntityChange(entity);
                }
            } catch {
                if (requestId !== entityChangeWarningRequestIdRef.current) {
                    return;
                }
                setPendingEntity(entity);
                setWarnings(null);
                setErrorMessage(
                    "Unable to verify transfer compatibility. Please try again.",
                );
            } finally {
                if (requestId === entityChangeWarningRequestIdRef.current) {
                    setIsVerifyingEntityChange(false);
                }
            }
        },
        [
            account.id,
            account.entityId,
            dismiss,
            performEntityChange,
            refuseIfViewOnly,
        ],
    );

    const isBlocked = Boolean(warnings?.transferError ?? errorMessage);

    const confirmationModal = (
        <ConfirmationModal
            show={!!pendingEntity && (!!warnings || !!errorMessage)}
            onHide={dismiss}
            title="Change entity"
            question={
                <EntityChangeWarningMessage
                    warnings={warnings}
                    error={errorMessage}
                />
            }
            yes={isBlocked ? "Close" : "Continue"}
            no={isBlocked ? undefined : "Cancel"}
            onConfirm={() => {
                if (isBlocked) {
                    dismiss();
                    return;
                }
                if (pendingEntity) {
                    performEntityChange(pendingEntity);
                }
            }}
            onReject={isBlocked ? undefined : dismiss}
        />
    );

    return {
        initiateEntityChange,
        confirmationModal,
        isVerifyingEntityChange,
    };
}
